import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSafari } from 'react-device-detect';
import { Close, PlayArrow } from '@material-ui/icons';
import tinycolor from 'tinycolor2';
import moment from 'moment';

import { getHighQCoverFromRenditions, getHighQThumbnail, getHighQThumbnailFromResource } from '../../util/media-data-util';
import { MessageHelper } from '../../util/message-helper';
import { getEmbedShareSettings, getPreviewLink, isTouchable, joinClassNames, toHHMMSS } from '../../util/util';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { usePlayer } from '../../contexts/PlayerContext';
import { defaultLivePreCover, publicDataDomain } from '../../util/config';
import { DateTimeHelper } from '../../util/date-time-helper';
import ControlButton from '../Controls/ControlButton';
import { EMAIL_KEY, HTTP_STATUS, SubmitFormDataProps, RegisterFormProps } from './types';

import DynamicForm from './DynamicForm';
import useRegistrationGuard from './useRegistrationGuard';

import './RegisterForm.scss';

const RegisterForm = ({ playerConfigs, isMobile, playerId }: RegisterFormProps) => {
    const { t: translator, i18n } = useTranslation();
    const {
        isMediaLive,
        mediasData,
        mediaName,
        currentMediaId,
        mediaType,
        accountId,
        configParams,
        embedShareSettings: embedShareSettingsMessage,
        setShowRegistrationForm,
        showRegistrationForm
    } = useConfiguration();

    const {
        currentMediaData,
        formTemplate,
        hasToken,
        isTokenValid,
		isValidatingToken,
        eventDate
    } = useRegistrationGuard();

    const { isPlaying } = usePlayer();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const dynamicFormContainerRef = useRef<HTMLDivElement | null>(null);
    const coverFormContainerRef = useRef<HTMLDivElement | null>(null);
    const formInfoContainerRef = useRef<HTMLDivElement | null>(null);
    const hasSentCloseSoftboxMessage = useRef(false);
    const [formData, setFormData] = useState<Record<string, string>>({});
    const [validations, setValidations] = useState<Record<string, string>>({});
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [existingEmail, setExistingEmail] = useState<string>('');
	const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
	const [isFormSuccess, setIsFormSuccess] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [showFullDescription, setShowFullDescription] = useState<boolean>(false);
    const [isDescriptionLong, setIsDescriptionLong] = useState<boolean>(false);
    const descriptionRef = useRef<HTMLDivElement | null>(null);
    const [modalFormHeight, setModalFormHeight] = useState<number | undefined>(undefined);

    const color = useMemo(() => (configParams?.formPrimaryColor || formTemplate?.settings?.buttonColor || playerConfigs?.playerStyling?.primaryColor || '#126cfc'), [configParams?.formPrimaryColor, formTemplate?.settings?.buttonColor, playerConfigs?.playerStyling?.primaryColor]);
    const opacity = Number(configParams?.primaryOpacity ?? playerConfigs?.playerStyling?.primaryOpacity ?? 100);
    const rgbaWithOpa = useMemo(() => ({ ...tinycolor(color).toRgb(), a: opacity / 100 }), [color, opacity]);
    const primaryColor = useMemo(() => tinycolor(rgbaWithOpa).toHexString(), [rgbaWithOpa]);
    const hoverStyle = useMemo(() => (isTouchable() ? {} : { backgroundColor: tinycolor(rgbaWithOpa).lighten().toHexString() }), [rgbaWithOpa]);

    const { backGroundImageUrl } = useMemo(() => {
        if (configParams.formCoverUrl) {
            return { backGroundImageUrl: configParams.formCoverUrl };
        }

        if (!isMediaLive) {
            let posterMedia = currentMediaData;

            const thumbnailMediaId = configParams?.thumbnailMediaId;
            if (thumbnailMediaId) {
                posterMedia = mediasData.find(({ id, originalId }: any) => id === thumbnailMediaId || originalId === thumbnailMediaId) ?? currentMediaData;
            }

            if (!posterMedia || !posterMedia?.mediaDetails) {
                return { backGroundImageUrl: "" };
            }

            if ((posterMedia.playlistAsset?.resources ?? []).length > 0) {
                const { resources } = posterMedia.playlistAsset;
                const thumbnailUrl = getHighQThumbnailFromResource(resources)
                return { backGroundImageUrl: thumbnailUrl };
            }
            const { posters, images } = posterMedia;
            const thumbnailUrl = getHighQThumbnail({ posters, images });
            return { backGroundImageUrl: thumbnailUrl };
        }

        if (!currentMediaData || (currentMediaData?.images ?? [])?.length === 0) {
            return { backGroundImageUrl: defaultLivePreCover };
        }

        const { images } = currentMediaData;
        return {
            backGroundImageUrl: getHighQCoverFromRenditions(images, "precover") || defaultLivePreCover,
        };
    }, [configParams?.formCoverUrl, configParams?.thumbnailMediaId, currentMediaData, isMediaLive, mediasData]);

    const { playIconLabel, showPlayIcon } = useMemo(() => {
        if (isMediaLive) {
            const isEventStarted = currentMediaData?.custom?.['event-start'] ? moment(currentMediaData?.custom?.['event-start']).diff(moment(), "second") <= 0 : true;
            return { playIconLabel: isEventStarted ? translator('LABEL_LIVE') : translator('LABEL_COMING'), showPlayIcon: isEventStarted };
        }
        return { playIconLabel: toHHMMSS(currentMediaData?.duration), showPlayIcon: true };
    }, [isMediaLive, currentMediaData, translator]);

    const isEmailValid = useCallback((email: string) => {
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return pattern.test(email);
    }, []);

    const validateForm = useCallback(() => {
        const validationMsgs: Record<string, string> = {};
        const uiSchema = formTemplate?.styling ?? { properties: {} };
        const schema = formTemplate?.schema ?? { properties: {} };
        for (const [key, value] of Object.entries(uiSchema.properties)) {
            if ((value as any).isRequired && (!formData[key] || !formData[key]?.length)) {
                const label = schema.properties[key].title;
                validationMsgs[key] = `${label} ${translator('DYNAMIC_FORM_VALID_REQUIRED_FIELD_MSG')}.`;
            } else {
                if (key === EMAIL_KEY && !isEmailValid(formData[key])) {
                    validationMsgs[key] = translator('DYNAMIC_FORM_VALID_EMAIL_MSG');
                }
            }
        }
        setValidations(validationMsgs);
        return !Object.keys(validationMsgs).length;
    }, [formData, formTemplate, translator, isEmailValid]);

    const submitFormData = async ({ accountId, formId, payload }: SubmitFormDataProps): Promise<any> => {
        const response = await fetch(`${publicDataDomain}${accountId}/forms/${formId}/registrations`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const responseStatus = response.status;
        if (responseStatus === HTTP_STATUS.CREATED) {
            return responseStatus;
        } else if (responseStatus === HTTP_STATUS.BAD_REQUEST) {
            const errorResponse = await response.json();
            return { ...errorResponse, status: responseStatus };
        }
        return response;
    };

    const embedShareSettings = getEmbedShareSettings({ configParams, messageData: embedShareSettingsMessage });
    const registrationHandler = useCallback((data: Record<string, string>) => {
		if (!formTemplate || !accountId) {
			return;
		}

		const fields = Object.entries(formTemplate.schema.properties).map(([key, value]: [string, any]) => {
			return {
				[key] : {
					label: value.title,
					value: data[key],
				}
			}
		}).reduce((result, curr) => {
			const [key, value] = Object.entries(curr)[0];
			result[key] = value;
			return result;
		}, {} as Record<string, { label: string, value: string }>);

        const params = {
            mediaDetails: {
                id: mediaType === 'scenario' ? configParams.scenarioId : configParams.mediaId,
                title: mediaName,
            },
            configs: { id: configParams.configId, ...playerConfigs },
            paramConfigs: configParams,
            accountId: accountId,
            type: mediaType,
            time: undefined,
            embedShareSettings: embedShareSettings,
        };
		const payload = {
			fields,
			eventDate: eventDate ? DateTimeHelper.localToUtc(eventDate, 'YYYY-MM-DDTHH:mm:ss[Z]') : null,
			eventLink: configParams.eventLink || getPreviewLink(params),
			mediaId: currentMediaId,
		};

		setIsLoading(true);
		setIsFormSubmitted(true);
		submitFormData({ accountId, formId: formTemplate?.id || '', payload })
			.then((res: any) => {
				if (res === HTTP_STATUS.CREATED || res.status === HTTP_STATUS.CREATED) {
					setIsFormSuccess(true);
				} else if (res.status === HTTP_STATUS.BAD_REQUEST) {
					setExistingEmail(payload.fields.email.value);
                    if (res.errors?.EventLink?.[0]) {
                        setErrorMsg(res.errors?.EventLink[0]);
                    }
				} else {
					throw new Error(`HTTP error! status: ${status}`);
				}
			})
			.catch((error: unknown) => {
				// Handle unexpected errors from submitFormData or status processing
				// eslint-disable-next-line no-console
				console.error('Failed to submit registration form', error);
			})
			.finally(() => {
				setIsLoading(false);
			});
    }, [accountId, configParams, currentMediaId, embedShareSettings, eventDate, formTemplate, mediaName, mediaType, playerConfigs]);

	const resetForm = () => {
		setIsFormSuccess(false);
		setIsFormSubmitted(false);
		setExistingEmail('');
	};

    const resendEmail = () => {
        setExistingEmail('');
        setIsFormSubmitted(false);
        setIsFormSuccess(false);
    };

    const handleSubmit = useCallback(() => {
        if (validateForm()) {
            registrationHandler(formData);
        }
    }, [formData, registrationHandler, validateForm]);

    const eventStart = currentMediaData?.custom?.['event-start'];
    const [isEventStarted, setIsEventStarted] = useState<boolean>(() => (eventStart ? moment(eventStart).diff(moment(), "second") <= 0 : true));

    // only called once the form template is fetched (media has a form) and the token check has finished
    const checkToShowForm = () => {
        if (formTemplate?.optional && isEventStarted) {
            setShowRegistrationForm(false);
            return;
        }

        // a valid token hides the form; an invalid token or no token shows it
        setShowRegistrationForm(!(hasToken && isTokenValid));
    };

    useEffect(() => {
        const formLanguage = formTemplate?.settings?.language;
        if (!showRegistrationForm || !formLanguage || formLanguage === i18n.language) {
            return;
        }
        i18n.changeLanguage(formLanguage);
        document.documentElement.lang = formLanguage;
    }, [showRegistrationForm, formTemplate?.settings?.language, i18n.language]);

    useEffect(() => {
        if (!isPlaying || !showRegistrationForm) {
            return;
        }
        playerId && (window as any).GoBrain.widgets(playerId)?.pause?.();
    }, [showRegistrationForm, isPlaying, playerId]);

    // the event start only changes the result for an optional form without a valid token (shown before start, hidden after),
    // so only poll in that case, every 5s until the event starts
    useEffect(() => {
        const getIsEventStarted = () => (eventStart ? moment(eventStart).diff(moment(), "second") <= 0 : true);
        setIsEventStarted(getIsEventStarted());

        const shouldWatchEventStart = !!formTemplate?.optional && !isValidatingToken && !(hasToken && isTokenValid);
        if (!shouldWatchEventStart || getIsEventStarted()) {
            return;
        }
        const eventStartInterval = setInterval(() => {
            const hasStarted = getIsEventStarted();
            setIsEventStarted(hasStarted);
            hasStarted && clearInterval(eventStartInterval);
        }, 5000);

        return () => clearInterval(eventStartInterval);
    }, [eventStart, formTemplate?.optional, hasToken, isTokenValid, isValidatingToken]);

    // only decide once the form template is fetched and the token check has finished
    useEffect(() => {
        if (!formTemplate || isValidatingToken) {
            return;
        }
        checkToShowForm();
    }, [formTemplate, hasToken, isTokenValid, isValidatingToken, isEventStarted]);

    useEffect(() => {
        if (!configParams.containerKey || configParams.qplayerPreview) {
            return;
        }
		const embedElement = document.querySelector(`qplayer-embed[data-qplayer-key="${configParams.containerKey}"]`);

        if (embedElement instanceof HTMLElement && embedElement.style.height !== 'auto' && showRegistrationForm) {
            embedElement.style.height = 'auto';
        }
        if (embedElement instanceof HTMLElement && embedElement.style.height === 'auto' && !showRegistrationForm) {
            embedElement.style.height = '';
        }
    }, [configParams.containerKey, configParams.qplayerPreview, showRegistrationForm]);

    const renderMessages = useCallback(() => {
        // Render message after form submission
        if (hasToken) {
            return <div
                className='qplayer-register__message'
                style={{ backgroundColor: tinycolor.mix(primaryColor ?? '#126cfc', '#ffffff', 97.5).toString() }}
            >
                {isValidatingToken && <div>{translator('REGISTRATION_PAGE_VERYFYING_TOKEN')}...</div>}
                {!isValidatingToken && !isTokenValid && formTemplate !== undefined && <div>{translator('REGISTRATION_PAGE_INVALID_TOKEN_MSG')}</div>}
            </div>;
        }

        if (isFormSubmitted && !hasToken) {
            if (isLoading) {
                return <div
                    className='qplayer-register__message'
		            style={{ backgroundColor: tinycolor.mix(primaryColor ?? '#126cfc', '#ffffff', 97.5).toString() }}
                >{translator('REGISTRATION_PAGE_LOADING')}...</div>;
            }
            if (existingEmail || errorMsg) {
                return <div
                    className='qplayer-register__message'
		            style={{ backgroundColor: tinycolor.mix(primaryColor ?? '#126cfc', '#ffffff', 97.5).toString() }}
                >
                    {errorMsg ? <div>{errorMsg}</div> : <div>{translator('REGISTRATION_PAGE_EXISTS_MSG')}: {existingEmail}</div>}
                    {!errorMsg && <ControlButton
                        className='qplayer-register-form__submit-button'
                        style={{ backgroundColor: primaryColor, color: formTemplate?.settings?.buttonTextColor || '#ffffff' }}
                        hoverStyle={hoverStyle}
                        onClick={resetForm}
                    >
                        {translator('REGISTRATION_PAGE_ANOTHER_EMAIL_MSG')}
                    </ControlButton>}
                </div>;
            }
            if (isFormSuccess) {
                return <div
                    className='qplayer-register__message'
		            style={{ backgroundColor: tinycolor.mix(primaryColor ?? '#126cfc', '#ffffff', 97.5).toString() }}
                >
                    <div className={configParams.isBonnierDi ? "qplayer-bold-font" : "qplayer-register__text--bold"}>{translator('REGISTRATION_PAGE_SUCCESS_MSG')}</div>
                    <div>{`${translator('REGISTRATION_PAGE_RECEIVE_EMAIL_MSG')} ${formData.email}`}</div>
                    <div style={{ marginTop: "16px" }}>{translator('REGISTRATION_PAGE_NOT_RECEIVE_EMAIL_MSG')} <span className='qplayer-register__resend-email' onClick={resendEmail}>{translator('REGISTRATION_PAGE_RESEND_EMAIL_MSG')}</span>.</div>
                </div>;
            }
        }
    }, [errorMsg, existingEmail, hasToken, hoverStyle, isFormSubmitted, isFormSuccess, isLoading, isTokenValid, isValidatingToken, primaryColor, translator, formData.email, formTemplate?.settings?.buttonTextColor, configParams.isBonnierDi]);

    const eventTitle = useMemo(() => configParams?.formTitle || configParams?.mediaTitle || currentMediaData?.mediaName, [currentMediaData?.mediaName, configParams?.formTitle, configParams?.mediaTitle]);
    const eventDescription = useMemo(() => configParams?.formDescription || configParams?.mediaDescription || currentMediaData?.description, [currentMediaData?.description, configParams?.formDescription, configParams?.mediaDescription]);
    const eventDateLabel = useMemo(() => {
        if (!eventDate) {
            return '';
        }

        return DateTimeHelper.formatDate(eventDate, translator, i18n.language);
    }, [eventDate, i18n.language, translator]);

    const onFieldChange = useCallback((key: string, value: string) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    }, []);

    useEffect(() => {
        if (!descriptionRef.current || !showRegistrationForm) {
            setIsDescriptionLong(false);
            return;
        }
        setIsDescriptionLong(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight);
    }, [eventDescription, showFullDescription, showRegistrationForm]);

    useEffect(() => {
        if (showRegistrationForm && dynamicFormContainerRef.current && !hasSentCloseSoftboxMessage.current) {
            hasSentCloseSoftboxMessage.current = true;
            const formContainerHeight = (coverFormContainerRef.current?.clientHeight ?? 0) + (formInfoContainerRef.current?.clientHeight ?? 0);
            MessageHelper.sendMessage({
                type: MessageHelper.MESSAGE_TYPES.hasFormContainer,
                mediaId: currentMediaId,
                metadata: {
                    formContainerHeight: formContainerHeight,
                    containerHeight: containerRef.current?.clientHeight,
                    dynamicFormContainerHeight: dynamicFormContainerRef.current.clientHeight,
                    coverFormContainerHeight: coverFormContainerRef.current?.clientHeight,
                    formInfoContainerHeight: formInfoContainerRef.current?.clientHeight,
                },
                qplayerId: configParams.qplayerId
            });
        }
    }, [showRegistrationForm, formTemplate, hasToken, isFormSubmitted, currentMediaId, configParams.qplayerId]);

    useEffect(() => {
        if (Object.keys(validations).length && dynamicFormContainerRef.current) {
            const formContainerHeight = (coverFormContainerRef.current?.clientHeight ?? 0) + (formInfoContainerRef.current?.clientHeight ?? 0);
            MessageHelper.sendMessage({
                type: MessageHelper.MESSAGE_TYPES.hasFormContainer,
                mediaId: currentMediaId,
                metadata: {
                    formContainerHeight: formContainerHeight,
                    containerHeight: containerRef.current?.clientHeight,
                    dynamicFormContainerHeight: dynamicFormContainerRef.current.clientHeight,
                    coverFormContainerHeight: coverFormContainerRef.current?.clientHeight,
                    formInfoContainerHeight: formInfoContainerRef.current?.clientHeight,
                },
                qplayerId: configParams.qplayerId
            });
        }
    }, [configParams.qplayerId, currentMediaId, validations]);

    const shouldFitFormToWindow = !!isMobile && (configParams.isModalInstance || configParams.qplayerPreview === 'true');

    useEffect(() => {
        if (!isMobile || !showRegistrationForm) {
            setModalFormHeight(undefined);
            return;
        }
        const updateModalFormHeight = () => {
            setModalFormHeight(Math.max(window.innerHeight - (coverFormContainerRef.current?.offsetHeight ?? 220), 0));
        };
        updateModalFormHeight();
        window.addEventListener('resize', updateModalFormHeight);
        window.addEventListener('orientationchange', updateModalFormHeight);
        // the cover height changes once its image loads, which shifts the space left for the form
        const coverResizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateModalFormHeight) : null;
        if (coverFormContainerRef.current) {
            coverResizeObserver?.observe(coverFormContainerRef.current);
        }
        return () => {
            window.removeEventListener('resize', updateModalFormHeight);
            window.removeEventListener('orientationchange', updateModalFormHeight);
            coverResizeObserver?.disconnect();
        };
    }, [isMobile, showRegistrationForm]);

    if (showRegistrationForm !== true) { // check explicitly for true to avoid the case of showRegistrationForm is undefined at the beginning, which will cause the player to flash before hiding when showRegistrationForm is set to false later
        return null;
    }

    return <div
        ref={containerRef}
        className={joinClassNames(`qplayer-register-container`, configParams.qplayerPreview ? 'qplayer-register-container--preview' : "", shouldFitFormToWindow ? 'qplayer-register-container--fit-window' : "", isMobile ? 'qplayer-register-container--mobile' : 'qplayer-register-container--desktop')}
    >
        <div className="qplayer-register__content">
            <div className="qplayer-register__cover" ref={coverFormContainerRef}>
                <img
                    className={joinClassNames("qplayer-register__cover-image", !backGroundImageUrl ? 'qplayer-register__cover-image--no-thumbnail' : '')}
                    src={backGroundImageUrl}
                    alt={eventTitle}
                />
                <span
                    className={joinClassNames('qplayer-register__cover-btn', configParams.isBonnierDi ? "qplayer-bold-font" : "")}
                    style={{ color: primaryColor }}
                >
                    {showPlayIcon && <PlayArrow htmlColor={primaryColor} />}{playIconLabel}
                </span>
            </div>
            <div
                className="qplayer-register__form"
                ref={formInfoContainerRef}
                style={modalFormHeight !== undefined && shouldFitFormToWindow ? { height: modalFormHeight } : undefined}
            >
                <div className="qplayer-register__event-info">
                    <div
                        className={joinClassNames('qplayer-register__title', configParams.isBonnierDi ? 'qplayer-register__title--di' : '')}
                        role="heading"
                        aria-level={1}
                    >
                        {eventTitle}
                    </div>
                    {eventDateLabel && <div className="qplayer-register__schedule">
                        <span className={configParams.isBonnierDi ? "qplayer-bold-font" : "qplayer-register__text--bold"}>{`${translator('REGISTRATION_PAGE_EVENT_DATE_LABEL')}: `}</span>{eventDateLabel}
                    </div>}
                    {eventDescription && (
                        <div
                            ref={descriptionRef}
                            className={joinClassNames('qplayer-register__description', !showFullDescription ? 'qplayer-register__description--clamped' : '')}
                        >
                            {eventDescription}
                            {isDescriptionLong && !showFullDescription && (
                                <span className={"qplayer-register__description-see-more"} onClick={() => setShowFullDescription(true)}>
                                    {translator('REGISTRATION_PAGE_SEE_MORE')}
                                </span>
                            )}
                            {showFullDescription && (
                                <span className={"qplayer-register__description-see-less"} onClick={() => setShowFullDescription(false)}>
                                    {translator('REGISTRATION_PAGE_SEE_LESS')}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {(hasToken || isFormSubmitted) && renderMessages()}

                {formTemplate && !hasToken && !isFormSubmitted && <DynamicForm
                    isMobile={isMobile}
                    schema={formTemplate?.schema ?? { properties: {} }}
                    uiSchema={formTemplate?.styling ?? { properties: {} }}
                    settings={formTemplate?.settings}
                    formData={formData}
                    primaryColor={primaryColor}
                    hoverStyle={hoverStyle}
                    validations={validations}
                    onFieldChange={onFieldChange}
                    onSubmit={handleSubmit}
                    containerRef={dynamicFormContainerRef}
                />}
            </div>
        </div>
        {configParams.inline === "off" && !!configParams.container && isMobile && <ControlButton
            tabIndex={0}
            ariaLabel={translator('LABEL_CLOSE')}
            tooltip={translator('LABEL_CLOSE')}
            hoverStyle={hoverStyle}
            onClick={() => {
                if (configParams.container) {
                    configParams.container.setAttribute('data-qplayer-modal-open', 'false');
                }
            }}
            icon={<Close htmlColor="currentColor" className={isSafari ? 'safari-fix' : ''} />}
            className='qplayer-close-modal'
            style={configParams.useLoader === "true" ? { visibility: "hidden" } : { position: "absolute", top: 0, right: 0, width: "32px" }}
            topControl
        />}
    </div>;
};

export default RegisterForm;